import { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import { LANGS, useI18n } from "../lib/i18n";

/**
 * Compact language switcher for the top toolbar — a globe button that opens a
 * menu of every locale. Selecting one persists to localStorage and flips
 * <html lang/dir> via the i18n provider, so the whole app re-renders in the
 * chosen language (RTL for ar/fa/ur). Menu aligns to the inline-end edge so it
 * never overflows the viewport in either direction.
 *
 * With 27 languages the list needs a height cap + its own scroll (otherwise it
 * runs off-screen) and a filter box — scanning 27 endonyms in a dropdown is
 * slow, and typing "esp" / "türk" / "日本" is not.
 */
export default function LangPicker() {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  // Match the endonym AND the code, so "de", "deutsch", "日本" all find it.
  const q = filter.trim().toLowerCase();
  const shown = q
    ? LANGS.filter((l) => l.label.toLowerCase().includes(q) || l.code.includes(q))
    : LANGS;

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Clear the filter when the menu closes, so it never reopens pre-filtered.
  useEffect(() => {
    if (!open) setFilter("");
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={t("top.language")}
        aria-label={t("top.language")}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center justify-center h-9 w-9 rounded-lg border text-parchment-muted hover:text-parchment hover:bg-surface-2 transition"
        style={{ backgroundColor: "rgb(var(--surface-1))", borderColor: "rgb(var(--border-soft))" }}
      >
        <Globe className="w-4 h-4" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 mt-2 w-56 rounded-xl p-1 shadow-xl z-50 flex flex-col max-h-[70vh]"
          style={{
            backgroundColor: "rgb(var(--bg-800))",
            border: "1px solid rgb(var(--border-mid))",
            boxShadow: "var(--shadow-glass-hover)",
          }}
        >
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t("common.search")}
            aria-label={t("common.search")}
            autoFocus
            className="input mb-1 h-8 text-sm shrink-0"
          />

          <div className="overflow-y-auto min-h-0">
          {shown.length === 0 && (
            <p className="px-3 py-2 text-sm text-parchment-muted">{t("tz.noMatches")}</p>
          )}
          {shown.map((l) => (
            <button
              key={l.code}
              type="button"
              role="menuitemradio"
              aria-checked={l.code === lang}
              onClick={() => {
                setLang(l.code);
                setOpen(false);
              }}
              dir={l.dir}
              className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-semibold text-parchment hover:bg-surface-2 transition"
            >
              <span>{l.label}</span>
              {l.code === lang && <Check className="w-4 h-4 text-gold-500 shrink-0" />}
            </button>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}
